import {
  WSMarginProps,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { WSTableColumn } from "@wingspanhq/fe-component-library/dist/lib/components/WSTable/types";
import {
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus,
  TinType
} from "@wingspanhq/payments/dist/interfaces";
import { UserStatus } from "@wingspanhq/users/dist/lib/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React from "react";
import { useHistory } from "react-router-dom";
import { useFocusedFeatures } from "../../../../FocusedFeatures";
import { useUserId } from "../../../../query/hooks/helpers";
import { usePayeesDownloadCSV } from "../../../../query/payee/mutations/usePayeesDownloadCSV";
import { useResendPayeeInvite } from "../../../../query/payee/mutations/useResendPayeeInvite";
import { getCountryName } from "../../../../query/users/selectors";
import {
  IPayeeRow,
  PayeeRowsQuery,
  PayerPayeeEngagementSearchableStatus,
  PayerPayeeSearchableRequirementsStatus,
  PayerPayeeSearchableTinStatus
} from "../../../../services/search";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { usePayeeRowActions } from "../../routes/RoutePayeeDetails/usePayeeRowActions";
import { useGoToPayeeDetails } from "../../utils";

export interface PayeeRowsTableProps extends WSMarginProps {
  isArchivedOnly?: boolean;
  filters?: PayeeRowsQuery;
  payees: IPayeeRow[];
  loading?: boolean;
}

export const PayeeRowsTable: React.FC<PayeeRowsTableProps> = ({
  filters,
  payees,
  isArchivedOnly,
  loading,
  ...elementProps
}) => {
  const userId = useUserId();
  const history = useHistory();
  const [downloadCSV, downloadCSVMeta] = usePayeesDownloadCSV();
  const [resendInvite, resendInviteMeta] = useResendPayeeInvite();
  const focusedFeatures = useFocusedFeatures();
  const isOrganizationContext = useXWingspanExpansion();
  const goToPayeeDetails = useGoToPayeeDetails();
  const getRowActions = usePayeeRowActions();

  const tableData = payees.map(payee => {
    return {
      data: payee,
      id: payee.payeeId
    };
  });

  return (
    <WSTable<IPayeeRow>
      loading={loading}
      tableData={tableData}
      onRowClick={({ data }) => {
        const isOrganizationPayee =
          !!isOrganizationContext &&
          !!data.payer &&
          !!data.payerId &&
          data.payerId !== userId;

        goToPayeeDetails(
          data.payeeId,
          isOrganizationPayee ? data.payerId : undefined
        );
      }}
      headerAction={{
        icon: "download",
        onClick: async () => {
          await downloadCSV({
            fileName: isArchivedOnly ? "Archived contractors" : "Contractors",
            ...filters
          });
        },
        loading: downloadCSVMeta.isLoading
      }}
      columns={[
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "1.5fr",
            header: {
              text: "Name"
            }
          },
          renderFunction: ({ data }) => {
            const names = wsName({
              user: data.user!,
              member: data.member,
              payerOwnedData: data.payerOwnedData
            });
            return (
              <WSTableCell
                avatar={{
                  type: "text",
                  text: names.getResolvedName(true) || "--"
                }}
                text={names.getResolvedName(true)}
                secondaryText={names.getResolvedSecondaryName()}
              />
            );
          }
        },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "1fr",
            header: {
              text: isOrganizationContext ? "Organization" : "Contact"
            },
            hideOnScreens: ["XS"]
          },
          renderFunction: ({ data }) => {
            const isOrganizationPayee =
              !!isOrganizationContext &&
              !!data.payer &&
              !!data.payerId &&
              data.payerId !== userId;

            if (isOrganizationContext) {
              let name = "-";
              if (data.payer?.user && isOrganizationPayee) {
                const names = wsName({
                  user: data.payer.user,
                  member: data.payer.member
                });

                name = names.getResolvedName();
              }

              return <WSTableCell text={name} />;
            } else {
              return <WSTableCell text={data.user?.email} />;
            }
          }
        },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "1fr",
            header: {
              text: "Tax ID"
            }
          },
          renderFunction: ({ data }) => {
            return <TaxIdCell data={data} />;
          }
        },
        {
          config: {
            gridTemplateSizeMin: "0",
            gridTemplateSizeMax: "1fr",
            header: {
              text: "Location"
            },
            hideOnScreens: ["XS"]
          },
          renderFunction: ({ data }) => (
            <WSTableCell
              text={data.member?.profile?.address?.city}
              secondaryText={
                data.member?.profile?.address?.country
                  ? getCountryName(data.member?.profile?.address?.country)
                  : undefined
              }
            />
          )
        },
        // {
        //   config: {
        //     gridTemplateSizeMin: "0",
        //     gridTemplateSizeMax: "1fr",
        //     header: {
        //       text: "Engagements"
        //     }
        //   },
        //   renderFunction: ({ data }) => (
        //     <WSTableCell
        //       text={data.engagements?.[0]?.name}
        //       secondaryText={
        //         data.engagements?.length! > 1
        //           ? `${data.engagements?.length! - 1} more`
        //           : undefined
        //       }
        //     />
        //   )
        // },
        // always show archived column on archived page for clarity
        ...(focusedFeatures.showContractorsEligibility || isArchivedOnly
          ? ([
              {
                config: {
                  gridTemplateSizeMin: "0",
                  gridTemplateSizeMax: "1fr",
                  header: {
                    text: "Requirements"
                  }
                },
                renderFunction: ({ data }) => {
                  return <RequirementCell data={data} />;
                }
              }
            ] as Array<WSTableColumn<IPayeeRow>>)
          : [])
      ]}
      rowActions={({ data }) =>
        data.user.status === UserStatus.Pending &&
        data.payerOwnedData.status !== PayerPayeeStatus.Inactive
          ? [
              {
                text: "Resend invite",
                onAsyncClick: async () => {
                  await resendInvite(data.payeeId);
                }
              }
            ]
          : []
      }
      rowMenuActions={({ data }) => {
        return getRowActions({
          payee: data,
          backPath: history.location.pathname
        });
      }}
      {...elementProps}
    />
  );
};

export const TaxIdCell: React.FC<{ data: IPayeeRow }> = ({ data }) => {
  const isShared =
    data.payeeOwnedData.shareTaxDocument ===
    PayeeTaxDocumentSharePermission.Allow;
  const secondaryText = isShared ? "Contractor shared" : "Provided by you";
  const tin = isShared
    ? data?.payeeOwnedData?.payeeW9Data?.tinType === TinType.Business
      ? data?.payeeOwnedData?.payeeW9Data?.einLastFour
      : data?.payeeOwnedData?.payeeW9Data?.ssnLastFour
    : data?.payerOwnedData?.payeeW9Data?.tinType === TinType.Business
    ? data?.payerOwnedData?.payeeW9Data?.einLastFour
    : data?.payerOwnedData?.payeeW9Data?.ssnLastFour;

  if (data.searchableTinStatus === PayerPayeeSearchableTinStatus.Pending) {
    return (
      <WSTableCell
        pill={{
          icon: "time",
          theme: "neutral",
          text: `${tin ? `•••${tin} ` : ""}Pending`
        }}
        secondaryText={secondaryText}
      />
    );
  } else if (
    data.searchableTinStatus === PayerPayeeSearchableTinStatus.Verified
  ) {
    return (
      <WSTableCell
        pill={{
          icon: true,
          theme: "success",
          text: `${tin ? `•••${tin} ` : ""}Matched`
        }}
        secondaryText={secondaryText}
      />
    );
  } else if (
    data.searchableTinStatus === PayerPayeeSearchableTinStatus.Failed
  ) {
    return (
      <WSTableCell
        pill={{
          icon: true,
          theme: "warning",
          text: `${tin ? `•••${tin} ` : ""}Mismatch`
        }}
        secondaryText={secondaryText}
      />
    );
  } else {
    return <WSTableCell text={undefined} />;
  }
};

export const RequirementCell: React.FC<{ data: IPayeeRow }> = ({ data }) => {
  if (data.searchableStatus === PayerPayeeEngagementSearchableStatus.Inactive) {
    return (
      <WSTableCell
        pill={{
          icon: "archive",
          theme: "neutral",
          text: "Archived"
        }}
      />
    );
  }

  if (
    data.searchableRequirementsStatus ===
    PayerPayeeSearchableRequirementsStatus.Complete
  ) {
    if (
      data.searchableStatus === PayerPayeeEngagementSearchableStatus.NotSignedUp
    ) {
      return (
        <WSTableCell
          pill={{
            icon: true,
            theme: "success",
            text: "Complete"
          }}
          secondaryText="Not signed up"
        />
      );
    }

    return (
      <WSTableCell
        pill={{
          icon: true,
          theme: "success",
          text: "Complete"
        }}
        secondaryText="All requirements complete"
      />
    );
  }

  if (
    data.searchableRequirementsStatus ===
    PayerPayeeSearchableRequirementsStatus.Incomplete
  ) {
    if (
      data.searchableStatus === PayerPayeeEngagementSearchableStatus.NotSignedUp
    ) {
      return (
        <WSTableCell
          pill={{
            icon: true,
            theme: "warning",
            text: "Incomplete"
          }}
          secondaryText="Not yet signed up"
          secondaryTextColor="amber400"
        />
      );
    }

    return (
      <WSTableCell
        pill={{
          icon: true,
          theme: "warning",
          text: "Incomplete"
        }}
        secondaryText="Awaiting requirements completion"
        secondaryTextColor="amber400"
      />
    );
  }

  return <WSTableCell text="--" />;
};
